// Fonts used from: https://github.com/fontsource/fontsource/tree/main/packages/roboto
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/700.css';
import { GoogleOAuthProvider } from '@react-oauth/google';
import CartModal from '@src/cart/components/cartModal';
import CheckoutModal from '@src/checkout/checkoutModal';
import CallBoxBanner from '@src/components/callbox/CallBoxBanner';
import LoginModal from '@src/components/loginModal';
import ResetPasswordModal from '@src/components/resetPasswordModal';
import SurveyBox from '@src/components/survey/SurveyBox';
import '@src/i18n';
import {
  filterLocaleNodesSubjects,
  findLocaleNodes,
} from '@src/i18n/localeList';
import { getGoogleToken, getUser } from '@src/services/auth';
import { UserProvider } from '@src/services/userContext';
import TeacherVideoModal from '@src/teachers/teacherVideoModal';
import '@src/theme/styles.scss';
import api from '@src/utils/api';
import { graphql, useStaticQuery } from 'gatsby';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Footer from './Footer/Footer';
import Navbar from './Navbar/Navbar.tsx';
import GoogleOneTap from './googleOneTap';

import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import { Flex } from 'theme-ui';

const Layout = ({
  children,
  locale,
  otherSlugs,
  currentSlug,
  seoTitle,
  seoDescription,
  navTheme = 'light',
}) => {
  const data = useStaticQuery(graphql`
    query {
      allContentfulMokomasisDalykas(sort: { name: ASC }) {
        nodes {
          ...SubjectsInfo
          navIcon: icon {
            gatsbyImageData(
              width: 60
              quality: 100
              placeholder: BLURRED
              formats: [AUTO, WEBP]
            )
          }
        }
      }
      allContentfulPuslapis {
        nodes {
          slug
          title
          node_locale
          layout
        }
      }
      mainMenu: allContentfulMeniu(
        filter: { placement: { eq: "Pagrindinis" } }
      ) {
        nodes {
          ...NavItem
        }
      }
      footerMenu: allContentfulMeniu(filter: { placement: { eq: "Poraštė" } }) {
        nodes {
          ...NavItem
        }
      }
      homePage: allContentfulPuslapis(
        filter: { layout: { eq: "Pagrindinis puslapis" } }
      ) {
        nodes {
          node_locale
          slug
        }
      }
      allContentfulSupportCallbox {
        nodes {
          id
          phone
          title
          bannerText
          chatBtnText
          description {
            raw
          }
          avatar {
            url
          }
          node_locale
        }
      }
    }
  `);

  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }

    if (
      location &&
      location.hostname &&
      location.hostname == 'www.mokosi.com'
    ) {
      window.location.href = 'https://www.mokosi.lt/en';
    }
  }, []);
  const subjectsData = filterLocaleNodesSubjects(
    data.allContentfulMokomasisDalykas.nodes,
    locale
  );
  const mainMeniuData = findLocaleNodes(data.mainMenu.nodes, locale);
  const footerMeniuData = findLocaleNodes(data.footerMenu.nodes, locale);
  const supportCallboxData = findLocaleNodes(
    data.allContentfulSupportCallbox.nodes,
    locale
  );
  // const homePageData = findLocaleNodes(data.homePage.nodes, locale);
  const materialPage = mainMeniuData?.items?.find(
    (node) => node.layout === 'Mokomosios medžiagos paieška'
  );

  const homePageData = { slug: '' };

  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isResetPassOpen, setIsResetPassOpen] = useState(false);

  const languageTitleMap = {
    en: 'English',
    lt: 'Lietuviškai',
    ro: 'Română',
  };

  const enSlug = otherSlugs.find((slug) => slug.locale === 'en');
  let supportedLanguages = otherSlugs.map((item) => ({
    locale: item.locale,
    slug: item.slug,
    text: languageTitleMap[item.locale],
  }));

  supportedLanguages = supportedLanguages.filter((item) => {
    if (item.locale === 'en') return true;
    if (['404', '403', '500'].includes(item.slug)) return true;
    if (item.slug === enSlug?.slug) return false;
    return true;
  });

  const user = getUser();

  // subjects with counts
  const [subjectsViewModel, setSubjectsViewModel] = useState(
    subjectsData.map((subject) => {
      return {
        name: subject.name,
        slug: subject.slug,
        icon: subject.navIcon,
        image: subject.icon,
        id: subject.uniqueId,
        count: null,
      };
    })
  );
  const [bannerOpen, setBannerOpen] = useState(false);
  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        let countsResponse = null;

        countsResponse = await api.get('teachers/counts', {
          controller,
        });
        const updatedSubjectsViewModel = [];
        subjectsViewModel.forEach((subject) => {
          const subjectRes =
            countsResponse.find((apiRes) => subject.id === apiRes.subject) || 0;

          updatedSubjectsViewModel.push({
            ...subject,
            count: subjectRes.count,
          });
        });
        setSubjectsViewModel(updatedSubjectsViewModel);
      } catch (error) {
        console.log(error);
      }
    };
    load();

    return () => controller?.abort();
  }, []);

  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        subjectswithcounts: subjectsViewModel,
      });
    }
    return child;
  });
  //   <meta
  //     key="facebook-domain-verification"
  //     name="facebook-domain-verification"
  //     content="qfz49mzilx3u3nntl6ycu3kygjuu9v"
  //   />,
  return (
    <GoogleOAuthProvider clientId={process.env.GATSBY_GOOGLE_CLIENT_ID}>
      <UserProvider>
        {!user?.userToken && !getGoogleToken() && <GoogleOneTap />}
        <Flex sx={{ flexDirection: 'column', minHeight: '100vh' }}>
          <Helmet
            htmlAttributes={{
              lang: locale,
            }}
            title={seoTitle ? seoTitle : t('siteDefaultTitle')}
            link={[
              { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' },
              {
                rel: 'apple-touch-icon',
                sizes: '180x180',
                href: '/favicon.ico',
              },
              {
                rel: 'icon',
                type: 'image/png',
                sizes: '32x32',
                href: '/favicon-32x32.png',
              },
              {
                rel: 'icon',
                type: 'image/png',
                sizes: '16x16',
                href: '/favicon-16x16.png',
              },
            ]}
            meta={[
              {
                name: `description`,
                content: seoDescription
                  ? seoDescription
                  : t('siteDefaultDescription'),
              },
              {
                name: 'facebook-domain-verification',
                content: 'qfz49mzilx3u3nntl6ycu3kygjuu9v',
              },
            ]}
          />
          <Navbar
            subjects={subjectsViewModel}
            languages={supportedLanguages}
            pageLocale={locale}
            supportBanner={
              <CallBoxBanner
                isOpen={bannerOpen}
                text={supportCallboxData?.bannerText}
                phone={supportCallboxData?.phone}
              />
            }
            homePageSlug={homePageData.slug}
            currentSlug={currentSlug}
            onLoginIn={() => {
              setIsLoginOpen(true);
            }}
            navTheme={navTheme}
            infoPages={mainMeniuData}
          />
          <SurveyBox />
          <main sx={{ marginBottom: 'auto', paddingTop: ['55px', '60px'] }}>
            {childrenWithProps}
          </main>
          <CheckoutModal subjects={subjectsData} />
          <CartModal subjects={subjectsData} materialPage={materialPage} />
          <LoginModal
            isOpen={isLoginOpen}
            onReset={() => {
              setIsResetPassOpen(true);
            }}
            onClose={() => {
              setIsLoginOpen(false);
            }}
          />
          <ResetPasswordModal
            isOpen={isResetPassOpen}
            onClose={() => {
              setIsResetPassOpen(false);
            }}
          />
          <TeacherVideoModal />
          <Footer
            subjects={subjectsData}
            locale={locale}
            homePageSlug={homePageData.slug}
            currentSlug={currentSlug}
            languages={supportedLanguages}
            onLoginIn={() => {
              setIsLoginOpen(true);
            }}
            infoPages={footerMeniuData}
          />
        </Flex>
      </UserProvider>
    </GoogleOAuthProvider>
  );
};

Layout.propTypes = {
  children: PropTypes.node,
  locale: PropTypes.string,
  otherSlugs: PropTypes.any,
  currentSlug: PropTypes.string,
  navTheme: PropTypes.string,
};

export default Layout;
